import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Plus } from 'lucide-react';

const REGTECH_NAV = [
  { label: 'Home', kind: 'anchor', target: 'home' },
  { label: 'Services', kind: 'anchor', target: 'services' },
  { label: 'About', kind: 'anchor', target: 'about' },
];

const INDUSTRIAL_NAV = [
  { label: 'Home', kind: 'route', target: '/industrial' },
  { label: 'Industrial Pumps', kind: 'route', target: '/industrial/pumps' },
  { label: 'Headcore B2B Matchmaking', kind: 'route', target: '/industrial/headcore' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isIndustrial = location.pathname.startsWith('/industrial') || location.pathname === '/pumping';
  const navItems = isIndustrial ? INDUSTRIAL_NAV : REGTECH_NAV;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const closeMobile = () => setIsMobileMenuOpen(false);

  return (
    <>
      {/* Main Header */}
      <header className="sticky top-0 z-50 bg-[#2d2d2d] border-b border-[#3eb5a0]/20 overflow-hidden relative">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden opacity-15">
          {/* Grid Pattern */}
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(62,181,160,0.2)" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>

          {/* Animated Nodes and Connections */}
          <div className="absolute inset-0">
            {/* Node 1 */}
            <div className="absolute top-1/4 left-[15%] w-2 h-2 bg-[#3eb5a0] rounded-full animate-pulse" 
                 style={{ animationDuration: '2s', animationDelay: '0s' }} />
            {/* Node 2 */}
            <div className="absolute top-1/2 left-[35%] w-2 h-2 bg-[#3eb5a0] rounded-full animate-pulse" 
                 style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} />
            {/* Node 3 */}
            <div className="absolute top-3/4 left-[55%] w-2 h-2 bg-[#3eb5a0] rounded-full animate-pulse" 
                 style={{ animationDuration: '2s', animationDelay: '1s' }} />
            {/* Node 4 */}
            <div className="absolute top-1/3 left-[75%] w-2 h-2 bg-[#3eb5a0] rounded-full animate-pulse" 
                 style={{ animationDuration: '3s', animationDelay: '0.3s' }} />
            {/* Node 5 */}
            <div className="absolute top-2/3 left-[85%] w-2 h-2 bg-[#3eb5a0] rounded-full animate-pulse" 
                 style={{ animationDuration: '2.5s', animationDelay: '0.8s' }} />

            {/* Connecting Lines */}
            <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <line x1="15%" y1="25%" x2="35%" y2="50%" stroke="rgba(62,181,160,0.3)" strokeWidth="1" 
                    strokeDasharray="5,5" className="animate-dash" />
              <line x1="35%" y1="50%" x2="55%" y2="75%" stroke="rgba(62,181,160,0.3)" strokeWidth="1" 
                    strokeDasharray="5,5" className="animate-dash" style={{ animationDelay: '0.5s' }} />
              <line x1="55%" y1="75%" x2="75%" y2="33%" stroke="rgba(62,181,160,0.3)" strokeWidth="1" 
                    strokeDasharray="5,5" className="animate-dash" style={{ animationDelay: '1s' }} />
              <line x1="75%" y1="33%" x2="85%" y2="66%" stroke="rgba(62,181,160,0.3)" strokeWidth="1" 
                    strokeDasharray="5,5" className="animate-dash" style={{ animationDelay: '1.5s' }} />
            </svg>
          </div>

          {/* Floating Data Particles */}
          <div className="absolute inset-0">
            <div className="absolute top-[20%] left-[10%] w-1 h-1 bg-[#3eb5a0]/50 rounded-full animate-float" 
                 style={{ animationDuration: '8s', animationDelay: '0s' }} />
            <div className="absolute top-[40%] left-[30%] w-1 h-1 bg-[#3eb5a0]/50 rounded-full animate-float" 
                 style={{ animationDuration: '10s', animationDelay: '2s' }} />
            <div className="absolute top-[60%] left-[50%] w-1 h-1 bg-[#3eb5a0]/50 rounded-full animate-float" 
                 style={{ animationDuration: '12s', animationDelay: '4s' }} />
            <div className="absolute top-[30%] left-[70%] w-1 h-1 bg-[#3eb5a0]/50 rounded-full animate-float" 
                 style={{ animationDuration: '9s', animationDelay: '1s' }} />
            <div className="absolute top-[50%] left-[90%] w-1 h-1 bg-[#3eb5a0]/50 rounded-full animate-float" 
                 style={{ animationDuration: '11s', animationDelay: '3s' }} />
          </div>

          {/* Digital Flow Lines */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute h-px bg-gradient-to-r from-transparent via-[#3eb5a0]/30 to-transparent w-full top-[30%] animate-slide-right" 
                 style={{ animationDuration: '15s' }} />
            <div className="absolute h-px bg-gradient-to-r from-transparent via-[#3eb5a0]/30 to-transparent w-full top-[70%] animate-slide-left" 
                 style={{ animationDuration: '20s' }} />
          </div>
        </div>

        <div className="max-w-[1920px] mx-auto px-6 md:px-12 relative z-10">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img 
                src="https://customer-assets.emergentagent.com/job_reshadi-replica/artifacts/2d3adpeg_imageedit_14_6586610433.png"
                alt="Reshadi Logo"
                className="h-12 md:h-16 lg:h-20 w-auto transition-transform duration-300 group-hover:scale-105"
                style={{ filter: 'brightness(0) saturate(100%) invert(70%) sepia(38%) saturate(536%) hue-rotate(120deg) brightness(95%) contrast(89%)' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-16">
              {navItems.map((item) =>
                item.kind === 'route' ? (
                  <Link
                    key={item.label}
                    to={item.target}
                    className="text-[#3eb5a0] hover:text-[#3eb5a0]/70 transition-colors duration-300 font-normal text-lg"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => scrollToSection(item.target)}
                    className="text-[#3eb5a0] hover:text-[#3eb5a0]/70 transition-colors duration-300 font-normal text-lg"
                  >
                    {item.label}
                  </button>
                )
              )}
            </nav>

            {/* Contact Button */}
            <button
              onClick={() => scrollToSection('contact')}
              className="hidden md:flex items-center gap-2 px-8 py-3 bg-transparent border border-[#3eb5a0] text-[#3eb5a0] rounded-full hover:bg-[#3eb5a0] hover:text-[#2d2d2d] transition-all duration-300 font-normal"
            >
              CONTACT US
              <span className="w-5 h-5 rounded-full border border-current flex items-center justify-center">
                <Plus size={12} className="rotate-45" />
              </span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-[#3eb5a0] p-2"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-[#3eb5a0]/20">
              <nav className="flex flex-col gap-4">
                {navItems.map((item) =>
                  item.kind === 'route' ? (
                    <Link
                      key={item.label}
                      to={item.target}
                      onClick={closeMobile}
                      className="text-[#3eb5a0] hover:text-[#3eb5a0]/70 transition-colors duration-300 font-normal text-left"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      key={item.label}
                      onClick={() => scrollToSection(item.target)}
                      className="text-[#3eb5a0] hover:text-[#3eb5a0]/70 transition-colors duration-300 font-normal text-left"
                    >
                      {item.label}
                    </button>
                  )
                )}
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-[#3eb5a0] hover:text-[#3eb5a0]/70 transition-colors duration-300 font-normal text-left"
                >
                  Contact Us
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;